const {
  BODY_ELEMENTS, ELEMENT_CLASSES,
} = require('../analyticsConst');

const { default: getFrontConfig } = require('../../common/getFrontConfig');
const { analyticsEventCenter, ANALYTICS_EVENTS } = require('../events');

const {
  vue: { TheProductList: { bannerTypes } },
} = getFrontConfig();

/**
 * Context of the impression observer
 * @readonly
 * @enum {string}
 */
const IMPRESSIONS_CONTEXTS = {
  CONTEXT_PLP: 'impression-context-plp',
  CONTEXT_SEARCH: 'impression-context-search',
  CONTEXT_RECOMMENDATION: 'impression-context-recommendation',
};

/**
 * @typedef {object} ImpressionObserverConfig
 * @property {IMPRESSIONS_CONTEXTS} context The context of the observed products
 */

/**
 * Init impressionsObserver module
 * @param {ImpressionObserverConfig} [configs] The configuration object
 */
function init(configs = {}) {
  /* Creating a new IntersectionObserver object. */
  const impressionsObserver = new IntersectionObserver(
    impressionsObserverCallback,
    { threshold: 0.5 },
  );

  /* Creating a new MutationObserver object. */
  const nodesObserver = new MutationObserver(nodesObserverCallback);

  const observedNodes = [];

  /**
   * Callback for impressions IntersectionObserver
   * If the element is visible, push visibility event to dataLayer and stop observing it
   * @param {IntersectionObserverEntry[]} entries - An array of IntersectionObserverEntry objects
   */
  function impressionsObserverCallback(entries) {
    entries.forEach((entry) => {
      const alreadyObserved = observedNodes.includes(entry.target.dataset.pid);
      if (entry.isIntersecting && !alreadyObserved) {
        impressionsObserver.unobserve(entry.target);
        if (entry.target.dataset.pid) {
          analyticsEventCenter.publish(
            analyticsEventCenter.EVENTS.productImpressionViewed,
            {
              pid: entry.target.dataset.pid,
              observerConfig: configs,
            },
          );
          observedNodes.push(entry.target.dataset.pid);
        }
        // Custom impression events
        if (entry.target.matches(BODY_ELEMENTS.gridBanner)
          || entry.target.matches(BODY_ELEMENTS.gridBannerProducts)) {
          customImpressionsObserverCallback(entry);
        }
      }
    });
  }

  /**
   * Callback for custom impressions IntersectionObserver
   * If the element is visible, push visibility event to dataLayer
   * @param {IntersectionObserverEntry} entry - IntersectionObserverEntry object
   */
  function customImpressionsObserverCallback(entry) {
    let eventName;
    let position = '';
    let type;
    const mainImageElement = entry.target.querySelector(
      BODY_ELEMENTS.gridBannerMainImg
    );
    if (!mainImageElement?.dataset) {
      return;
    }
    const { url } = mainImageElement.dataset;

    if (entry.target.matches(BODY_ELEMENTS.gridBanner)) {
      if (entry.target.dataset.bannerType === bannerTypes.plpMobileSlider) {
        // Carousel
        eventName = ANALYTICS_EVENTS.productListBannerCarouselViewed;
        type = ANALYTICS_EVENTS.PAYLOADS.productListBannerCarouselViewed
          .type.bannerCarousel;
        position = parseInt(entry.target.dataset?.key?.split('-')?.pop(), 10);
      } else {
        // Banner image
        eventName = ANALYTICS_EVENTS.productListBannerViewed;
        if (entry.target.dataset.bannerType === bannerTypes.plpFullImage) {
          type = ANALYTICS_EVENTS.PAYLOADS.productListBannerViewed
            .type.bannerImageFull;
        } else if (
          entry.target.dataset.bannerType === bannerTypes.plpDoubleImage
        ) {
          type = ANALYTICS_EVENTS.PAYLOADS.productListBannerViewed
            .type.bannerImageDouble;
        } else if (
          entry.target.dataset.bannerType === bannerTypes.plpSimpleImage
        ) {
          type = ANALYTICS_EVENTS.PAYLOADS.productListBannerViewed
            .type.bannerImageSimple;
        }
      }
    }
    // Send info to Analytics
    analyticsEventCenter.publish(
      eventName,
      {
        position,
        type,
        url,
      },
    );
  }

  /**
   * If a new node is added to the DOM, and that node has a class of 'base-product-grid__item',
   * update product tiles nodelist
   * @param {MutationRecord[]} mutationsList - An array of MutationRecord objects, each of which contains information about
   * a mutation that occurred on the DOM.
   */
  function nodesObserverCallback(mutationsList) {
    mutationsList.forEach((mutation) => {
      mutation.addedNodes.forEach((addedNode) => {
        if (
          addedNode.classList?.contains(ELEMENT_CLASSES.productGridItem)
          || addedNode.classList?.contains(ELEMENT_CLASSES.productSliderItem)
          || addedNode.classList?.contains(ELEMENT_CLASSES.productTile)
        ) {
          if (addedNode.querySelector(BODY_ELEMENTS.gridBanner)) {
            impressionsObserver.observe(addedNode.querySelector(BODY_ELEMENTS.gridBanner));
          } else if (addedNode.matches(BODY_ELEMENTS.gridBannerProducts)) {
            impressionsObserver.observe(addedNode);
          }
          addedNode.querySelectorAll(BODY_ELEMENTS.productTile).forEach((productTile) => {
            impressionsObserver.observe(productTile);
          });
        }
      });
    });
  }

  /**
   * The function initializes the observer by observing each product tile
   * and then observing the container for changes in the DOM
   * @param {HTMLElement} container impressions container
   */
  function observe(container) {
    if (container) {
      // Observe current product tiles and banners
      const productTiles = container.querySelectorAll(
        `${BODY_ELEMENTS.productTile}, ${BODY_ELEMENTS.gridBanner}, ${BODY_ELEMENTS.gridBannerProducts}`,
      );
      productTiles.forEach((productTile) => impressionsObserver.observe(productTile));
      // Observe added product tiles
      nodesObserver.observe(
        container,
        { subtree: true, childList: true },
      );
    }
  }

  /**
   * It disconnects IntersectionObserver and MutationObserver from the DOM
   */
  function disconnect() {
    impressionsObserver.disconnect();
    nodesObserver.disconnect();
    observedNodes.length = 0;
  }

  return {
    observe,
    disconnect,
  };
}

module.exports = {
  IMPRESSIONS_CONTEXTS,
  init,
};
